import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createAnnouncement, createFeed, searchMembersForFeed, updateFeed } from '../../../../../util/APIUtils';
import { debounce } from '@material-ui/core';
import { deduplicateSearchMembersAndGroupLocationsHistory, getErrorMsg, getUserDataFromType, joinFeedRoom } from '../../services/Feed.service';
import store, { changeToastType, showToast } from '../../../../core/store';
import { USER_CONTEXT } from '../../../../../constants';
import { buildMemberNames, mapCreateResponseToFeed } from '../../mappers/feedUtility';
import { FeedTypeEnum } from '@orijinworks/frontend-commons';
import { MEMBERSHIP_TYPE } from '../../constants';

export const useManageFeedsHook = (directMessageProps, tabNavigation, refetchAnnouncements) => {
    const { setConversations, feedDetails, userDetail } = directMessageProps;
    const SEARCH_ITEM_DELAY = 500;
    const DEFAULT_NUMBER_OF_MESSAGES_ALLOWED = 3;
    const [searchedMembers, setSearchMembers] = useState([]);
    const [isMemberSearchLoading, setMemberSearchLoading] = useState(false);
    const [memberCount, setMemberCount] = useState(0);
    const [isManageFeedLoading, setManageFeedLoading] = useState(false);

    const onSearchChangeHandler = (text) => {
        searchItem(text);
    };

    const searchItem = useMemo(
        () =>
            debounce(async (value) => {
                setMemberSearchLoading(true);
                const type = tabNavigation?.activeTab == "Messages" ? 'PERSON' : undefined;
                const memberFeeds = await searchMembersForFeed(value, type);
                memberFeeds.data = deduplicateSearchMembersAndGroupLocationsHistory(memberFeeds);
                console.log("memberFeeds", memberFeeds);
                setMemberSearchLoading(false);
                if (memberFeeds.status == 200) {
                    setSearchMembers(memberFeeds.data);
                    return;
                }
                store.dispatch(changeToastType("error"));
                store.dispatch(showToast(getErrorMsg(memberFeeds.status)));
            }, SEARCH_ITEM_DELAY),
        [tabNavigation?.activeTab]
    );

    const onSaveChanges = useCallback(async ({ conversationSettings }) => {
        setManageFeedLoading(true);
        const updateFeedPayload = {
            "name": feedDetails.name,
            "metadata": feedDetails.metadata,
            "permissions": {
                "messaging_disabled": conversationSettings.shutDownConversation,
                "learners_can_send_message": conversationSettings.learnerCanSendMessages,
                "learner_can_reply": feedDetails.permissions.learner_can_reply,
                "limit_learner_messages": conversationSettings.limitLearnerMessages,
                "learner_message_limit": conversationSettings.numberOfMessages,
            },
        }
        try {
            const updatedFeed = await updateFeed(feedDetails.id, USER_CONTEXT.personId, updateFeedPayload);
            if (updatedFeed?.status == 200) {
                // Refresh feed details from server to get the latest state
                if (directMessageProps.refreshFeedDetails) {
                    await directMessageProps.refreshFeedDetails(feedDetails.id);
                } else {
                    // Fallback to local update if refreshFeedDetails is not available
                    directMessageProps.handleFeedDetailsLoaded({
                        ...feedDetails,
                        permissions: updateFeedPayload.permissions
                    });
                }
                store.dispatch(changeToastType("success"));
                store.dispatch(showToast("Feed updated successfully!"));
                document.querySelector(`div[aria-label="close"]`)?.click();
                return;
            }
            store.dispatch(changeToastType("error"));
            store.dispatch(showToast("Something went wrong!"));
        } catch (e) {
            console.log(e);
        } finally {
            setManageFeedLoading(false);
        }
    }, [feedDetails, directMessageProps]);

    const onCreateHandler = useCallback(async ({ selectedMembers, conversationSettings }) => {
        setManageFeedLoading(true);
        const modifiedSelectedMemberCollection = Object.assign([], selectedMembers);
        const payload = {
            "feedType": "dm",
            "name": buildMemberNames(selectedMembers, USER_CONTEXT.personId, MEMBERSHIP_TYPE.PERSON,),//getUserDataFromType(modifiedSelectedMemberCollection, 'person', 'recordType').map(item => item.username).join(", "),
            "participantPersonKeys": getUserDataFromType(modifiedSelectedMemberCollection, 'person', 'recordType').map(item => item.id),
            "isConfidential": false,
            "metadata": {
                "description": ""
            },
            "permissions": {
                "messaging_disabled": false,
                "learners_can_send_message": conversationSettings.learnerCanSendMessages,
                "learner_can_reply": true,
                "limit_learner_messages": conversationSettings.limitLearnerMessages,
                "learner_message_limit": conversationSettings.numberOfMessages
            },
            "participantLocationKeys": getUserDataFromType(modifiedSelectedMemberCollection, 'location', 'type').map(item => item.id),
            "participantGroupKeys": getUserDataFromType(modifiedSelectedMemberCollection, 'learner_group', 'type').map(item => item.id)
        }
        try {
            const createdFeed = await createFeed(payload);
            if (createdFeed.status == 200) {
                store.dispatch(changeToastType("success"));
                store.dispatch(showToast("Feed created successfully!"));
                // Join the feed room to receive real-time messages
                joinFeedRoom([`feed_${createdFeed.data.id}`]);
                setConversations(conversations => {
                    const conversationIndex = conversations.findIndex(_conversation => _conversation.id == createdFeed.data.id);
                    if (conversationIndex > -1) {
                        conversations.splice(conversationIndex, 1);
                    }
                    conversations.unshift(mapCreateResponseToFeed(createdFeed.data, userDetail.userId, directMessageProps.openConversationRef));
                    return conversations
                });
                document.querySelector(`div[aria-label="close"]`)?.click();
                setTimeout(() => {
                    document.querySelector(`#feed-${createdFeed.data.id}`)?.click();
                }, 500);
                return;
            }
            store.dispatch(changeToastType("error"));
            store.dispatch(showToast("Something went wrong!"));
        } catch (e) {
            console.log(e);
        } finally {
            setManageFeedLoading(false);
        }
    }, [setConversations, directMessageProps]);

    const onFeedSettingEditHandler = useCallback(() => {
        // Handle cases where permissions might be null, undefined, or empty array
        const permissions = feedDetails?.permissions;
        const hasValidPermissions = permissions && typeof permissions === 'object' && !Array.isArray(permissions);
        
        const initialSettings = {
            "learnersCanSendMessage": hasValidPermissions ? permissions.learners_can_send_message : false,
            "messagingDisabled": hasValidPermissions ? permissions.messaging_disabled : false,
            "learnerCanReply": hasValidPermissions ? permissions.learner_can_reply : true,
            "learnerMessageLimit": hasValidPermissions ? permissions.learner_message_limit : DEFAULT_NUMBER_OF_MESSAGES_ALLOWED,
            "limitLearnerMessages": hasValidPermissions ? permissions.limit_learner_messages : true
        }

        return new Promise((resolve) => {
            setMemberCount(feedDetails?.memberCount);
            resolve(initialSettings);
        });
    }, [feedDetails]);

    const onCreateAnnouncement = useCallback(async (data) => {
        const { announcementTitle, editorContent, selectedMembers } = data;
        setManageFeedLoading(true);
        try {
            const payload = {
                subject: announcementTitle,
                body: editorContent,
                personKeys: getUserDataFromType(selectedMembers, 'person', 'recordType').map(item => item.id),
                locationKeys: getUserDataFromType(selectedMembers, 'location', 'type').map(item => item.id),
                groupKeys: getUserDataFromType(selectedMembers, 'group', 'type').map(item => item.id)
            }
            const announcementResonse = await createAnnouncement(payload);
            if (announcementResonse.status == 200) {
                document.querySelector(`div[aria-label="close"]`)?.click();
                store.dispatch(changeToastType("success"));
                store.dispatch(showToast(announcementResonse.message));
                
                // Refetch announcements to show the newly created one
                if (refetchAnnouncements) {
                    refetchAnnouncements();
                }
                return;
            }
            store.dispatch(changeToastType("error"));
            store.dispatch(showToast("Something went wrong while trying to create announcement, please try again after few minutes or try reloading the application!"));
        } catch (e) {
            console.log(e)
            store.dispatch(changeToastType("error"));
            store.dispatch(showToast("Something went wrong while trying to create announcement, please try again after few minutes or try reloading the application!"));
        } finally {
            setManageFeedLoading(false);
        }
    }, []);
    return {
        isManageFeedLoading,
        memberCount,
        DEFAULT_NUMBER_OF_MESSAGES_ALLOWED,
        searchedMembers,
        isMemberSearchLoading,
        onCreateAnnouncement,
        onSaveChanges,
        onSearchChangeHandler,
        onCreateHandler,
        onFeedSettingEditHandler
    };
};
